import { mapActions } from 'vuex'
import 'deep-chat'
import {
  applyAgentText,
  applyTaskEvent,
  applyToolEvent,
  buildQuestionAnswers,
  createActivity,
  createQuestionCard,
  finishActivity,
  isHiddenTool,
  isQuestionCardComplete,
  needsSubmitButton,
  parseAgentEvent,
  renderActivityHtml,
  renderQuestionHtml,
  toggleQuestionOption
} from './agentTimeline'
import { buildChatConfig, buildStopButton } from './chatTheme'

const ERROR_TEXT = 'Something went wrong here 😬 My bad! Please try again later 🫶'
const IDLE_PLACEHOLDER = 'Ask Gecko anything…'
const RUNNING_PLACEHOLDER = 'Add instructions…'
const MAX_FILE_BYTES = 25 * 1024 * 1024
const RUN_WATCHDOG_MS = 6 * 60 * 1000
const KEEPALIVE_MS = 4 * 60 * 1000
const STOP_ACK_TIMEOUT_MS = 20 * 1000

export default {
  props: {
    admin: {
      type: Boolean,
      default: false
    },
    pageId: {
      type: Number,
      default: null
    },
    articleId: {
      type: Number,
      default: null
    },
    showAgents: {
      type: Boolean,
      default: true
    },
    showSuggestions: {
      type: Boolean,
      default: true
    },
    systemMessage: {
      type: String,
      default: 'Let me help you make edits to this content. What do you have in mind?'
    },
    showSystemMessage: {
      type: Boolean,
      default: true
    },
    demo: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isRunning: false,
      isStopping: false
    }
  },
  computed: {
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    }
  },
  created () {
    this.history = []
    this.run = createRunState()
    this.stopAckTimer = null
    this.stopButton = buildStopButton(() => { this.stopCopilot() })
    this.demoAgent = null
    if (this.demo) import('./demoAgent').then(({ createDemoAgent }) => { this.demoAgent = createDemoAgent({ emit: payload => this.handleWebsocketMessage(payload) }) })
    this.unsubscribeAction = this.$store.subscribeAction({
      after: (action) => {
        if (action.type === 'handleWebsocketMessage' && action.payload) this.handleWebsocketMessage(action.payload)
      }
    })
  },
  mounted () {
    const chat = this.$refs.chat
    Object.assign(chat, buildChatConfig({ placeholder: IDLE_PLACEHOLDER }), {
      connect: { handler: this.connectChatHandler, websocket: true },
      customButtons: [this.stopButton],
      htmlClassUtilities: this.buildHtmlClassUtilities()
    })
    if (this.showSystemMessage) chat.introMessage = { text: this.systemMessage }
  },
  beforeDestroy () {
    if (this.unsubscribeAction) this.unsubscribeAction()
    this.stopTicker()
    this.clearStopAck()
    if (this.demoAgent) this.demoAgent.destroy()
  },
  methods: {
    ...mapActions([
      'sendWebsocketMessage',
      'uploadCopilotAttachment'
    ]),

    connectChatHandler (_, signals) {
      signals.onOpen()
      signals.newUserMessage.listener = () => this.handleUserSubmit()
    },

    buildHtmlClassUtilities () {
      return {
        'cg-activity-toggle': { events: { click: (event) => this.handleActivityToggle(event) } },
        'cg-q-option': { events: { click: (event) => this.handleQuestionOptionClick(event) } },
        'cg-q-submit': { events: { click: (event) => this.handleQuestionSubmit(event) } }
      }
    },

    async sendMessage (body) {
      if (this.demoAgent) return this.demoAgent.send(body)
      await this.sendWebsocketMessage(body)
    },

    async handleUserSubmit () {
      const { files, text } = getLastUserMessage(this.$refs.chat)
      if (!text && !files.length) return
      const oversized = files.find(file => file.size > MAX_FILE_BYTES)
      if (oversized) return this.addChatError(`${oversized.name} is larger than 25MB.`)

      const isSteering = this.isRunning
      if (isSteering) this.run.segment = null
      else this.startRun()

      try {
        const attachments = files.length ? await this.uploadAttachments(files) : []
        this.history.push({ role: 'user', content: text, ...(attachments.length ? { attachments } : {}) })
        this.$emit('messages-updated', this.history)
        await this.sendMessage({
          action: 'sendMessage',
          message: {
            ...this.buildScope(),
            messages: this.history.filter(m => m.content || m.attachments)
          }
        })
      } catch (err) {
        console.error(err)
        this.appendAssistantText(ERROR_TEXT)
        this.finishRun('error')
      }
    },

    buildScope () {
      if (this.admin) return { admin: true }
      return { workspaceId: this.workspaceId, contentplanId: this.contentplanId, clusterId: this.articleId, pageId: this.pageId }
    },

    uploadAttachments (files) {
      if (this.demoAgent) return files.map(file => ({ contentType: file.type, fileName: file.name, key: `demo/${file.name}`, sizeBytes: file.size }))
      return Promise.all(files.map(file => this.uploadCopilotAttachment(file)))
    },

    async stopCopilot () {
      if (!this.isRunning || this.isStopping) return
      this.isStopping = true
      this.awaitStopAck()
      this.finishRun('interrupted')
      this.appendStoppedNote()
      try {
        await this.sendMessage({ action: 'stopMessage' })
      } catch (err) {
        console.error(err)
        this.clearStopAck()
      }
    },

    awaitStopAck () {
      this.clearStopAck()
      this.stopAckTimer = setTimeout(() => this.clearStopAck(), STOP_ACK_TIMEOUT_MS)
    },

    clearStopAck () {
      if (this.stopAckTimer) clearTimeout(this.stopAckTimer)
      this.stopAckTimer = null
    },

    appendStoppedNote () {
      this.addChatMessage({ role: 'ai', text: 'Stopped.' })
      this.history.push({ role: 'assistant', content: 'Stopped.' })
      this.$emit('messages-updated', this.history)
    },

    startRun () {
      this.run = createRunState()
      this.isRunning = true
      this.isStopping = false
      this.stopButton.setState?.('default')
      this.$refs.chat.setPlaceholderText(RUNNING_PLACEHOLDER)
      this.openActivity()
      this.startTicker()
    },

    finishRun (status) {
      if (!this.isRunning) return
      const activityStatus = status === 'success' ? 'success' : status === 'interrupted' ? 'stopped' : 'error'
      for (const entry of this.run.activities) {
        if (entry.activity.status !== 'running') continue
        finishActivity(entry.activity, activityStatus)
        this.renderActivity(entry)
      }
      for (const entry of Object.values(this.run.questions)) {
        if (entry.card.status !== 'pending') continue
        entry.card.status = 'cancelled'
        this.renderQuestion(entry)
      }
      this.isRunning = false
      this.isStopping = false
      this.stopTicker()
      this.stopButton.setState?.('disabled')
      this.$refs.chat.setPlaceholderText(IDLE_PLACEHOLDER)
    },

    handleWebsocketMessage (response) {
      if (this.stopAckTimer) return this.handleMessageAwaitingStopAck(response)
      if (!this.isRunning) {
        if (response?.messages?.[0]?.role === 'data') this.$emit('data-message', response.messages[0])
        return
      }
      this.run.lastEventAt = Date.now()
      const message = response?.messages?.[0]
      if (!message) return
      if (message.role === 'data') return this.$emit('data-message', message)
      if (message.role === 'tool_status') return this.handleAgentEvent(parseAgentEvent(message.content))
      if (message.role === 'assistant') this.appendAssistantText(message.content)
    },

    handleMessageAwaitingStopAck (response) {
      const message = response?.messages?.[0]
      if (message?.role === 'data') return this.$emit('data-message', message)
      const event = message?.role === 'tool_status' ? parseAgentEvent(message.content) : null
      if (event?.kind === 'run' && event.phase === 'end') this.clearStopAck()
    },

    handleAgentEvent (event) {
      if (!event?.kind) return
      if (event.kind === 'tool') return this.handleToolEvent(event)
      if (event.kind === 'task') return this.updateActivityFor(event.toolUseId, activity => applyTaskEvent(activity, event))
      if (event.kind === 'agent_text') return this.updateActivityFor(event.parentToolUseId, activity => applyAgentText(activity, event))
      if (event.kind === 'question') return this.handleQuestionEvent(event)
      if (event.kind === 'run' && event.phase === 'end') this.finishRun(event.status)
    },

    handleToolEvent (event) {
      if (isHiddenTool(event.toolName)) return
      const knownEntry = this.run.toolActivity[event.toolUseId] || this.run.toolActivity[event.parentToolUseId]
      const entry = knownEntry || (event.phase === 'start' ? this.currentActivity() : null)
      if (!entry) return
      applyToolEvent(entry.activity, event)
      this.run.toolActivity[event.toolUseId] = entry
      this.renderActivity(entry)
    },

    updateActivityFor (toolUseId, update) {
      const entry = this.run.toolActivity[toolUseId]
      if (!entry) return
      update(entry.activity)
      this.renderActivity(entry)
    },

    currentActivity () {
      if (this.run.segment === 'activity') return this.run.activities[this.run.activities.length - 1]
      return this.openActivity()
    },

    openActivity () {
      this.settleOpenActivities()
      const activity = createActivity(`activity-${Date.now()}-${this.run.activities.length}`)
      const entry = { activity, index: this.addChatMessage({ html: renderActivityHtml(activity), role: 'ai' }) }
      this.run.activities.push(entry)
      this.run.segment = 'activity'
      return entry
    },

    settleOpenActivities () {
      for (const entry of this.run.activities) {
        if (entry.activity.status !== 'running') continue
        const hasRunningStep = Object.values(entry.activity.nodes).some(node => node.status === 'running')
        if (hasRunningStep) continue
        finishActivity(entry.activity, this.isStopping ? 'stopped' : 'success')
        this.renderActivity(entry)
      }
    },

    appendAssistantText (text) {
      if (!text) return
      if (this.run.segment !== 'text') {
        this.settleOpenActivities()
        this.run.text = text
        this.run.textIndex = this.addChatMessage({ role: 'ai', text })
        this.run.segment = 'text'
        this.history.push({ role: 'assistant', content: text })
      } else {
        this.run.text += text
        this.updateChatMessage({ text: this.run.text }, this.run.textIndex)
        this.history[this.history.length - 1].content += text
      }
      this.$emit('messages-updated', this.history)
    },

    handleQuestionEvent (event) {
      if (event.phase === 'ask') {
        this.settleOpenActivities()
        const card = createQuestionCard(event)
        const index = this.addChatMessage({ html: renderQuestionHtml(card), role: 'ai' })
        this.run.questions[event.toolUseId] = { card, index }
        this.run.segment = 'question'
        return
      }
      const entry = this.run.questions[event.toolUseId]
      if (!entry) return
      if (event.phase === 'expired') {
        entry.card.status = 'cancelled'
        return this.renderQuestion(entry)
      }
      if (event.phase !== 'answered') return
      entry.card.status = 'answered'
      entry.card.answers = event.answers
      this.renderQuestion(entry)
    },

    handleQuestionOptionClick (event) {
      const option = event.target?.closest?.('.cg-q-option')
      const entry = this.findQuestionEntry(event)
      if (!option || !entry) return
      toggleQuestionOption(entry.card, Number(option.dataset.qIndex), option.dataset.qLabel)
      if (!needsSubmitButton(entry.card)) return this.submitQuestion(entry)
      this.renderQuestion(entry)
    },

    handleQuestionSubmit (event) {
      const entry = this.findQuestionEntry(event)
      if (entry) this.submitQuestion(entry)
    },

    findQuestionEntry (event) {
      const toolUseId = event.target?.closest?.('.cg-question')?.dataset?.toolUseId
      return toolUseId ? this.run.questions[toolUseId] : null
    },

    async submitQuestion (entry) {
      if (entry.card.status !== 'pending' || !isQuestionCardComplete(entry.card)) return
      const answers = buildQuestionAnswers(entry.card)
      entry.card.status = 'answered'
      entry.card.answers = answers
      this.run.lastEventAt = Date.now()
      this.renderQuestion(entry)
      try {
        await this.sendMessage({ action: 'answerQuestion', answers, toolUseId: entry.card.toolUseId })
      } catch (err) {
        console.error(err)
        entry.card.status = 'pending'
        this.renderQuestion(entry)
      }
    },

    handleActivityToggle (event) {
      const id = event.target?.closest?.('.cg-activity')?.dataset?.activityId
      const entry = this.run.activities.find(e => e.activity.id === id)
      if (entry) entry.activity.collapsed = !entry.activity.collapsed
    },

    renderActivity (entry) {
      this.updateChatMessage({ html: renderActivityHtml(entry.activity) }, entry.index)
    },

    renderQuestion (entry) {
      this.updateChatMessage({ html: renderQuestionHtml(entry.card) }, entry.index)
    },

    updateChatMessage (body, index) {
      const chat = this.$refs.chat
      const container = chat.shadowRoot?.querySelector('#messages')
      const isNearBottom = container && container.scrollHeight - container.scrollTop - container.clientHeight < 120
      chat.updateMessage(body, index)
      if (isNearBottom) container.scrollTop = container.scrollHeight
    },

    addChatMessage (message) {
      const chat = this.$refs.chat
      chat.addMessage(message)
      return chat.getMessages().length - 1
    },

    addChatError (text) {
      this.$refs.chat.addMessage({ error: text })
    },

    startTicker () {
      this.stopTicker()
      this.ticker = setInterval(() => this.tick(), 1000)
    },

    sendKeepalive () {
      this.run.lastPingAt = Date.now()
      this.sendMessage({ action: 'ping' }).catch(err => console.error(err))
    },

    stopTicker () {
      if (this.ticker) clearInterval(this.ticker)
      this.ticker = null
    },

    tick () {
      for (const entry of this.run.activities) {
        if (entry.activity.status === 'running') this.renderActivity(entry)
      }
      if (Date.now() - this.run.lastPingAt > KEEPALIVE_MS) this.sendKeepalive()
      const isWaitingForUser = Object.values(this.run.questions).some(entry => entry.card.status === 'pending')
      if (!isWaitingForUser && Date.now() - this.run.lastEventAt > RUN_WATCHDOG_MS) {
        this.appendAssistantText(ERROR_TEXT)
        this.finishRun('error')
      }
    }
  }
}

const createRunState = () => ({
  activities: [],
  lastEventAt: Date.now(),
  lastPingAt: Date.now(),
  questions: {},
  segment: null,
  text: '',
  textIndex: -1,
  toolActivity: {}
})

const getLastUserMessage = (chat) => {
  const lastMessage = chat.getMessages().filter(message => message.role === 'user').pop() || {}
  const files = (lastMessage.files || []).map(file => file.ref).filter(ref => ref instanceof File)
  return { files, text: (lastMessage.text || '').trim() }
}
